import { enUS, es, ptBR } from 'date-fns/locale';
import { normalizeLanguage } from '../../constants/Languages';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

export const DATE_ADAPTER_LOCALES = { pt: ptBR, en: enUS, es };

export const getDateAdapterLocale = (language) =>
  DATE_ADAPTER_LOCALES[normalizeLanguage(language)] ?? enUS;

export const textFieldSx = {
  width: '100%',
  '& .MuiPickersInputBase-root': {
    height: '48px !important',
  },
  '& .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.line} !important`,
    borderWidth: '2px !important',
    borderRadius: '8px !important',
  },
  '&:hover .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
  },
  '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
    borderWidth: '2px !important',
  },
};

export const errorTextFieldSx = {
  ...textFieldSx,
  '& .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: '#f44336 !important',
    borderWidth: '2px !important',
    borderRadius: '8px !important',
  },
};

const groupedInnerLabelSx = {
  color: BRAND.textMuted,
  fontWeight: 500,
  fontSize: '0.9rem',
  '&.Mui-focused': {
    color: BRAND.accentText,
  },
};

const buildGroupedInnerFieldSx = ({ error = false, edge = 'start' } = {}) => {
  const borderColor = error ? '#f44336' : BRAND.line;
  const activeBorderColor = error ? '#f44336' : BRAND.accent;

  const outlineEdgeStyles = edge === 'start'
    ? {
      borderRadius: { xs: '8px 8px 0 0 !important', sm: '8px 0 0 8px !important' },
      borderRightWidth: { xs: '2px !important', sm: '0 !important' },
      borderBottomWidth: { xs: '0 !important', sm: '2px !important' },
    }
    : {
      borderRadius: { xs: '0 0 8px 8px !important', sm: '0 8px 8px 0 !important' },
      borderLeftWidth: { xs: '2px !important', sm: '0 !important' },
      borderTopWidth: { xs: '0 !important', sm: '2px !important' },
    };

  return {
    width: '100%',
    minWidth: 0,
    '& .MuiPickersInputBase-root': {
      height: '48px !important',
      minHeight: '48px !important',
      borderRadius: outlineEdgeStyles.borderRadius,
    },
    '& .MuiPickersSectionList-root': {
      padding: '12px 0',
    },
    '& .MuiPickersInputBase-sectionContent': {
      fontSize: '16px',
      lineHeight: '24px',
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: `${borderColor} !important`,
      borderWidth: '2px !important',
      transition: 'border-color 0.2s ease',
      ...outlineEdgeStyles,
    },
    '&:hover .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: `${activeBorderColor} !important`,
    },
    '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
      borderColor: `${activeBorderColor} !important`,
      borderWidth: '2px !important',
    },
    '& .MuiInputLabel-root': error
      ? {
          ...groupedInnerLabelSx,
          color: '#f44336',
          '&.Mui-focused': {
            color: '#f44336',
          },
        }
      : groupedInnerLabelSx,
  };
};

export const groupedInnerFieldSx = buildGroupedInnerFieldSx({ edge: 'start' });
export const groupedInnerEndFieldSx = buildGroupedInnerFieldSx({ edge: 'end' });
export const groupedInnerFieldErrorSx = buildGroupedInnerFieldSx({ error: true, edge: 'start' });
export const groupedInnerEndFieldErrorSx = buildGroupedInnerFieldSx({ error: true, edge: 'end' });

export const dateRangeGroupSx = () => ({
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  overflow: 'visible',
});

export const calendarSurfaceSx = {
  '& .MuiPickersCalendarHeader-root': {
    backgroundColor: BRAND.background,
    borderBottom: `1px solid ${BRAND.line}`,
  },
  '& .MuiPickersCalendarHeader-label': {
    fontWeight: 600,
    fontSize: '16px',
    color: BRAND.text,
  },
  '& .MuiIconButton-root': {
    color: BRAND.accentText,
    '&:hover': {
      backgroundColor: brandAlpha('accent', 0.1),
    },
  },
  '& .MuiPickersCalendarHeader-switchViewButton': {
    color: BRAND.accentText,
  },
  '& .MuiDayCalendar-header': {
    padding: '0',
    margin: '0',
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '0',
    width: '100%',
  },
  '& .MuiDayCalendar-weekDayLabel': {
    fontWeight: 500,
    fontSize: { xs: '11px', sm: '12px' },
    color: BRAND.textMuted,
    width: { xs: '36px', sm: '40px' },
    height: { xs: '36px', sm: '40px' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0',
    padding: '0',
    justifySelf: 'center',
  },
  '& .MuiDayCalendar-weekContainer': {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '0',
    width: '100%',
    '& .MuiPickersDay-root': {
      width: { xs: '36px', sm: '40px' },
      height: { xs: '36px', sm: '40px' },
      margin: '0',
      fontWeight: 500,
      fontSize: { xs: '13px', sm: '14px' },
      justifySelf: 'center',
      '&:hover': {
        backgroundColor: brandAlpha('accent', 0.1),
      },
      '&.Mui-selected': {
        backgroundColor: BRAND.accent,
        color: BRAND.onAccent,
        fontWeight: 600,
        '&:hover': {
          backgroundColor: BRAND.accentDark,
        },
      },
      '&.MuiPickersDay-today:not(.Mui-selected)': {
        border: `2px solid ${BRAND.accent}`,
        backgroundColor: 'transparent',
        color: BRAND.accentText,
        fontWeight: 600,
      },
    },
  },
  '& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected': {
    backgroundColor: BRAND.accent,
    color: BRAND.onAccent,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: BRAND.accentDark,
    },
  },
  '& .MuiPickersCalendar-root': {
    overflow: 'visible !important',
    maxHeight: 'none !important',
    height: 'auto !important',
  },
  '& .MuiDayCalendar-root': {
    overflow: 'visible !important',
    maxHeight: 'none !important',
    height: 'auto !important',
  },
  '& .MuiDayCalendar-monthContainer': {
    overflow: 'visible !important',
    maxHeight: 'none !important',
    height: 'auto !important',
  },
};

export const popperSx = {
  '& .MuiPaper-root': {
    borderRadius: BRAND_RADIUS.card,
    boxShadow: `0 8px 32px ${brandAlpha('primary', 0.12)}`,
    border: `1px solid ${BRAND.line}`,
    overflow: 'visible !important',
    maxHeight: 'none !important',
    height: 'auto !important',
    minWidth: { xs: '280px', sm: '320px' },
    maxWidth: { xs: '90vw', sm: 'none' },
  },
  ...calendarSurfaceSx,
};

export const getDatePickerLocaleText = (t) => ({
  okButtonLabel: t('common.datePicker.ok'),
  cancelButtonLabel: t('common.datePicker.cancel'),
  clearButtonLabel: t('common.datePicker.clear'),
  todayButtonLabel: t('common.datePicker.today'),
  previousMonth: t('common.datePicker.previousMonth'),
  nextMonth: t('common.datePicker.nextMonth'),
  toolbarTitle: t('common.datePicker.toolbarTitle'),
  selectDateLabel: t('common.datePicker.toolbarTitle'),
  openDatePickerDialogue: () => t('common.datePicker.openDatePickerDialogueEmpty'),
  closeDatePickerDialogue: t('common.datePicker.closeDatePickerDialogue'),
});
